import React from 'react';
import { GlassCard } from './GlassCard';

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  variant?: 'default' | 'compact' | 'horizontal';
  className?: string;
  iconClassName?: string;
}

export const FeatureCard: React.FC<FeatureCardProps> = ({
  icon,
  title,
  description,
  variant = 'default',
  className = '',
  iconClassName = '',
}) => {
  if (variant === 'horizontal') {
    return (
      <GlassCard variant="medium" hover className={`flex items-start gap-4 ${className}`}>
        <div
          className={`
            flex-shrink-0 w-12 h-12 rounded-xl
            bg-gradient-to-br from-primary-light/20 to-secondary-light/20
            dark:from-primary-dark/20 dark:to-secondary-dark/20
            flex items-center justify-center
            text-primary-light dark:text-primary-dark
            ${iconClassName}
          `}
        >
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-bold text-text-primary-light dark:text-text-primary-dark mb-1">
            {title}
          </h3>
          <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
            {description}
          </p>
        </div>
      </GlassCard>
    );
  }

  if (variant === 'compact') {
    return (
      <GlassCard variant="subtle" hover className={`text-center p-4 ${className}`}>
        <div
          className={`
            mx-auto w-10 h-10 rounded-lg mb-3
            bg-gradient-to-br from-primary-light/20 to-secondary-light/20
            dark:from-primary-dark/20 dark:to-secondary-dark/20
            flex items-center justify-center
            text-primary-light dark:text-primary-dark
            ${iconClassName}
          `}
        >
          {icon}
        </div>
        <h3 className="text-base font-bold text-text-primary-light dark:text-text-primary-dark mb-1">
          {title}
        </h3>
        <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
          {description}
        </p>
      </GlassCard>
    );
  }

  // Default variant
  return (
    <GlassCard variant="medium" hover className={`h-full ${className}`}>
      <div className="relative z-10 flex flex-col h-full">
        <div
          className={`
            mb-4 w-14 h-14 rounded-xl
            bg-gradient-to-br from-primary-light/20 to-secondary-light/20
            dark:from-primary-dark/20 dark:to-secondary-dark/20
            flex items-center justify-center
            text-primary-light dark:text-primary-dark
            group-hover:scale-110 transition-transform duration-300
            ${iconClassName}
          `}
        >
          {icon}
        </div>
        <h3 className="text-xl font-bold mb-2 text-text-primary-light dark:text-text-primary-dark">
          {title}
        </h3>
        <p className="text-text-secondary-light dark:text-text-secondary-dark text-sm flex-grow">
          {description}
        </p>
      </div>
    </GlassCard>
  );
};

export default FeatureCard;
